import {Alert, Link, Stack, styled} from "@mui/material";
import Grid from "@mui/material/Grid";
import HeaderBreadcrumb from "../components/HeaderBreadcrumb";
import AwardFilters from "../components/awards/AwardFilters";
import {useEffect, useRef, useState} from "react";
import AwardsHeadline from "../components/awards/AwardsHeadline";
import AwardsTabs from "../components/awards/AwardsTabs";
import AwardsDateFilters from "../components/awards/AwardsDateFilters";
import dayjs from "dayjs";
import {usePayAwardsData} from "../hooks/PayAwardsSearches";
import AwardsSectorSelection, {defaultPayAwardsLabel} from "../components/awards/AwardsSectorSelection";
import _ from "lodash";

const defaultFilters = {
    sort: "company_name",
    fromEffectiveDate: dayjs().subtract(12, "month").add(1, "day").format("YYYY-MM-DD"),
    toEffectiveDate: dayjs().format("YYYY-MM-DD"),
    increase: {min: 0, max: 30},
    increaseType: "All",
    ignoredAwards: [],
    unionRecognised: false,
    longTermDeals: false,
    employeeCount: {},
};

const RetryLink = styled(Link)(() => ({
    fontWeight: "bold",
    textTransform: "none",
    underline: "hover",
    '&:hover': {
        textDecoration: "underline",
        cursor: "pointer",
    }
}));

const PayAwards = () => {
    const [payAwards, setPayAwards] = useState({rows: []});
    const [filters, setFilters] = useState({...defaultFilters});
    const [bouncedFilters, setBouncedFilters] = useState({...defaultFilters});
    const [label, setLabel] = useState(defaultPayAwardsLabel);
    const [resetCount, setResetCount] = useState(0);

    const updateBouncedFilters = useRef(_.debounce(async (updatedFilters) => {
        setBouncedFilters(updatedFilters);
    }, 1000)).current;

    const updateFilters = (update = filters) => {
        const updatedFilters = {...update};
        setFilters(updatedFilters);
        updateBouncedFilters(updatedFilters);
    };

    const {data, error, isLoading} = usePayAwardsData(bouncedFilters);
    useEffect(() => {
        if (!isLoading) {
            setPayAwards(data);
        }
    }, [data, error, isLoading]);

    const resetFilters = () => {
        updateFilters(defaultFilters);
        setLabel(defaultPayAwardsLabel);
        setResetCount(resetCount + 1);
    };

    return (
        <Stack>
            <HeaderBreadcrumb name="Pay Awards" color="payAwards" resetFilters={resetFilters}/>
            <Stack sx={{padding: '1rem'}} spacing={1}>
                <AwardsHeadline data={payAwards} label={label}/>
                <AwardsDateFilters filters={filters} updateFilters={updateFilters}/>
                <AwardsSectorSelection filters={filters} updateFilters={updateFilters} updateLabel={setLabel}/>
                {error && (
                    <Alert severity="error">
                        There has been an error loading Pay Awards data. The application will automatically attempt
                        to reload the data. Alternatively, click <RetryLink underline="hover" onClick={() => setPayAwards({rows: []})}>Retry</RetryLink>.
                    </Alert>
                )}
                <Grid container spacing={1} columns={{xs: 1, md: 12}}>
                    <Grid size={{xs: 1, md: 2}}>
                        <AwardFilters filters={filters} updateFilters={updateFilters}/>
                    </Grid>
                    <Grid size={{xs: 1, md: 10}}>
                        <AwardsTabs key={resetCount} data={payAwards} isLoading={isLoading} label={label} filters={filters} updateFilters={updateFilters}/>
                    </Grid>
                </Grid>
            </Stack>
        </Stack>
    );
};

export default PayAwards;
